import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { IStepProps } from '../interfaces/Step'
import useStepFormHook from './useStepFormHook'
import { uploadState } from '../store/State/UploadState'
import {
  ALERT_MESSAGES,
  DOCUMENT_TYPE_ID_50,
  STATUS,
  STEP4_MAIN_PERSON_OPTIONS,
} from '../enums/common'
import { ILogementProps } from '../interfaces/Logement'
import { Step4Hook } from '../interfaces/Step4'
import { IUploadFormState } from '../interfaces/Upload'
import kycCore, { saveJddAddress } from '../services/Kyc'
import { IStore } from '../interfaces/Store'
import { loadingActions } from '../store/slices/Loading'
import { DefaultJddAddress } from '../store/State/JddAddress'
import { IJddAddress } from '../interfaces/Address'
import { parcoursAddressActions } from '../store/slices/ParcoursAddress'
import { handleError } from '../services/utils'

const Step4FormHook = (props: IStepProps): Step4Hook => {
  const [state, setState] = useState<IUploadFormState[]>([uploadState])
  const { form, onSubmit } = useStepFormHook(props)
  const {
    handleSubmit,
    register,
    errors,
    watch,
    triggerValidation,
    control,
    setValue,
    setError,
    clearError,
    formState: { dirtyFields },
  } = form
  const watchExistingValue = watch('existing_address')
  const { folderError, isMediumOrLowRisk } = useSelector(
    (store: IStore) => store.folderReducer
  )

  if (folderError) {
    Object.entries(folderError).forEach(([key, value]) => {
      setError(key, {
        message: value as string,
      })
    })
  }

  const history = useHistory()
  const dispatch = useDispatch()

  const validateFile = (index: number) => {
    if (!state[index]) {
      throw new Error('Index not found')
    }
    let valid = true
    const files = state[index].file

    files.forEach((file, i) => {
      if (!file) {
        valid = false
        setState((prevState) => {
          const indexPrevState = prevState[index]
          const newState = [...prevState]
          const { fileAlerts } = indexPrevState
          fileAlerts[i] = ALERT_MESSAGES.mandatory
          newState[index] = {
            ...indexPrevState,
            fileAlerts,
            validationButtonError: null,
          }
          return newState
        })
      }
    })

    return valid
  }

  const validateFiles = () => {
    return state.every((item) => item.status === STATUS.validated)
  }

  const logementProps: ILogementProps = {
    errors,
    register,
    watch,
    state,
    setState,
    validateFile,
    isMediumOrLowRisk,
    setValue,
  }

  const [isJddDocUploaded, setIsJddDocUploaded] = useState(false)
  const isJddUploaded = () => {
    const jddDocs = state[0]
    const jddIndexFile = jddDocs.docInfo.findIndex(
      (doc) => doc.typeId === DOCUMENT_TYPE_ID_50
    )

    const existingUploadedFile =
      jddDocs.file[jddIndexFile] !== null &&
      jddDocs.file[jddIndexFile]?.type === ''

    return (
      (watchExistingValue === '0' && jddDocs.status === STATUS.validated) ||
      existingUploadedFile
    )
  }
  useEffect(() => {
    setIsJddDocUploaded(isJddUploaded())
    // eslint-disable-next-line
  }, [state])

  const handleNextStep = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // there are no files to validate we can try to submit the form
    if (watchExistingValue === '1') {
      return
    }
    if (validateFiles()) {
      return
    }
    event.preventDefault()
    state.forEach((item, index: number) => {
      if (validateFile(index)) {
        setState((prevState) => {
          const indexPrevState = prevState[index]
          const newState = [...prevState]
          newState[index] = {
            ...indexPrevState,
            validationButtonError: ALERT_MESSAGES.validationButton,
          }
          return newState
        })
      }
    })
    await form.triggerValidation()
  }

  const submitForm = async (data: Record<string, any>) => {
    const filteredFormData = Object.keys(data).reduce((acc, key) => {
      if (
        (!dirtyFields.has(key) && key === 'email') ||
        (!dirtyFields.has(key) && key === 'telephone_mobile_1_num')
      ) {
        return acc
      }

      return { ...acc, [key]: data[key] }
    }, {} as Record<string, string>)

    const jddAddressFields = Object.keys(
      DefaultJddAddress
    ) as (keyof IJddAddress)[]
    const formData: Record<string, string> = {
      ...filteredFormData,
      ...(data.commune_habitation && {
        commune_habitation: data.commune_habitation.value,
      }),
    }
    const jddAddressData: IJddAddress = { ...DefaultJddAddress }
    let hasJddAddressFields = false
    jddAddressFields.forEach((key) => {
      if (key in data) {
        hasJddAddressFields = true
        jddAddressData[key] =
          key === 'commune_habitation' ? data[key].value : data[key]
      } else {
        delete jddAddressData[key]
      }
      delete formData[key]
    })
    // if at least one jdd address field is found, call save jdd address endpoint
    if (hasJddAddressFields) {
      try {
        dispatch(loadingActions.loading())
        dispatch(parcoursAddressActions.addressLoading())
        const responseAddress = await saveJddAddress(jddAddressData)
        if (responseAddress) {
          dispatch(
            parcoursAddressActions.addressReceived({
              address: {
                country: jddAddressData.adresse_pays,
                city: jddAddressData.adresse_ville,
                zipcode: jddAddressData.adresse_cp,
                address: [
                  jddAddressData.adresse_batiment,
                  jddAddressData.adresse_rue,
                  jddAddressData.adresse_bp,
                ]
                  .filter(Boolean)
                  .join(', '),
              },
            })
          )
        } else {
          dispatch(parcoursAddressActions.addressLoaded())
        }
        dispatch(loadingActions.loaded())
      } catch (e) {
        dispatch(loadingActions.loaded())
        handleError(e, history)
      }
    }

    if (STEP4_MAIN_PERSON_OPTIONS.includes(data.situation_logement)) {
      const { person } = kycCore
      try {
        dispatch(loadingActions.loading())
        await person.deletePerson({ personTypeId: '2' })
        dispatch(loadingActions.loaded())
      } catch (e) {
        dispatch(loadingActions.loaded())
        handleError(e, history)
      }
    }
    return onSubmit(formData)
  }

  return {
    errors,
    handleNextStep,
    handleSubmit,
    logementProps,
    submitForm,
    register,
    triggerValidation,
    control,
    isJddDocUploaded,
    setValue,
    watch,
    setError,
    clearError,
  }
}

export default Step4FormHook
